// Sign up (FR-004 to FR-009, FR-016, FR-047 to FR-049; P-AUTH-1; R-3): name, email, password and a
// contact mobile, then Check your inbox naming the address (the same for a new and an existing
// address, SC-005), with Resend email, Change email and Log in. Only ever creates a parent account:
// there is no role choice (FR-008). The email is kept in memory only, never in the address.
import { Link, useLocalSearchParams } from "expo-router";
import { useState } from "react";
import { StyleSheet, View } from "react-native";

import { sanitizeReturnTo } from "@/session/access";
import { makeStyles } from "@/theme/makeStyles";
import { useCooldown } from "@/lib/useCooldown";
import { useSubmitAction } from "@/lib/useSubmitAction";
import { Button } from "@/ui/Button";
import { Form } from "@/ui/Form";
import { InlineMessage } from "@/ui/InlineMessage";
import { LinkButton } from "@/ui/LinkButton";
import { PasswordField } from "@/ui/PasswordField";
import { Text } from "@/ui/Text";
import { TextField } from "@/ui/TextField";

import { useAuthApi } from "../AuthApiContext";
import { AUTH_COPY } from "../copy";
import {
  type AuthErrorView,
  type AuthField,
  fieldMessage,
  messageFor,
} from "../messages";
import { AuthCard } from "../ui/AuthCard";
import { LegalLine, type LegalLinks, useLegalLinks } from "../ui/LegalLine";
import {
  checkEmail,
  checkMobile,
  checkName,
  checkNewPassword,
} from "../validation";

const RESEND_COOLDOWN_S = 60;

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    form: { gap: t.space.lg },
    actions: { gap: t.space.md },
  }),
);

type Fields = "name" | "email" | "password" | "mobile";
type FieldErrors = Partial<Record<Fields, string>>;
type Banner = Exclude<AuthErrorView, { kind: "fields" | "linkInvalid" }>;

export type SignUpScreenProps = {
  /** Tests inject the terms and privacy addresses; the app reads them from the public config. */
  legal?: LegalLinks | null;
};

function ErrorBanner({ view }: { view: Banner }) {
  if (view.kind === "connection")
    return <InlineMessage tone="warning">{view.text}</InlineMessage>;
  return (
    <InlineMessage
      tone="error"
      detail={
        view.kind === "banner" && view.reference
          ? AUTH_COPY.errors.reference(view.reference)
          : null
      }
    >
      {view.text}
    </InlineMessage>
  );
}

export function SignUpScreen({ legal }: SignUpScreenProps) {
  const styles = useStyles();
  const authApi = useAuthApi();
  const configuredLegal = useLegalLinks();
  const links = legal === undefined ? configuredLegal : legal;
  const { returnTo } = useLocalSearchParams<{ returnTo?: string }>();
  const safeReturnTo = sanitizeReturnTo(
    typeof returnTo === "string" ? returnTo : null,
  );
  const loginHref = {
    pathname: "/login" as const,
    params: safeReturnTo ? { returnTo: safeReturnTo } : {},
  };

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobile, setMobile] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState<Banner | null>(null);
  /** The address the email went to; set means the Check your inbox step. */
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const cooldown = useCooldown(RESEND_COOLDOWN_S);

  const register = useSubmitAction(
    (input: Parameters<typeof authApi.registerWithEmail>[0]) =>
      authApi.registerWithEmail(input),
  );
  const resend = useSubmitAction((address: string) =>
    authApi.resendVerificationEmail(address),
  );

  async function submit() {
    const n = checkName(name);
    const e = checkEmail(email);
    const p = checkNewPassword(password);
    const m = checkMobile(mobile);
    const errors: FieldErrors = {
      ...(n.ok ? {} : { name: fieldMessage("name", n.code) }),
      ...(e.ok ? {} : { email: fieldMessage("email", e.code) }),
      ...(p.ok ? {} : { password: fieldMessage("password", p.code) }),
      ...(m.ok ? {} : { mobile: fieldMessage("mobile", m.code) }),
    };
    setFieldErrors(errors);
    if (!n.ok || !e.ok || !p.ok || !m.ok) return;

    setBanner(null);
    const result = await register.submit({
      name: n.value,
      email: e.value,
      password: p.value,
      mobile: m.value,
    });
    if (result.status === "skipped") return;
    if (result.status === "done") {
      setSentTo(e.value);
      setResent(false);
      return;
    }
    const view = messageFor(result.error, "registerWithEmail");
    if (view.kind === "fields") {
      const fields: FieldErrors = {};
      for (const [field, text] of Object.entries(view.fields) as [
        AuthField,
        string,
      ][]) {
        if (field !== "confirmPassword") fields[field] = text;
      }
      setFieldErrors(fields);
      return;
    }
    if (view.kind === "linkInvalid") return;
    setBanner(view);
  }

  async function resendEmail() {
    if (sentTo === null || cooldown.active) return;
    setBanner(null);
    const result = await resend.submit(sentTo);
    if (result.status === "done") {
      setResent(true);
      cooldown.start();
    } else if (result.status === "failed") {
      const view = messageFor(result.error, "resendVerificationEmail");
      if (view.kind !== "fields" && view.kind !== "linkInvalid")
        setBanner(view);
    }
  }

  function changeEmail() {
    // FR-007: name, email and mobile stay; the password is typed again.
    setPassword("");
    setSentTo(null);
    setResent(false);
    setBanner(null);
    setFieldErrors({});
  }

  if (sentTo !== null) {
    return (
      <AuthCard title={AUTH_COPY.checkInbox.title} testID="check-inbox">
        <Text>{AUTH_COPY.checkInbox.sentTo(sentTo)}</Text>
        {resent ? (
          <InlineMessage tone="success">
            {AUTH_COPY.checkInbox.resent}
          </InlineMessage>
        ) : null}
        {banner ? <ErrorBanner view={banner} /> : null}
        <View style={styles.actions}>
          <Button
            variant="outline"
            onPress={() => void resendEmail()}
            pending={resend.pending}
            disabled={cooldown.active}
            testID="check-inbox-resend"
          >
            {cooldown.active
              ? AUTH_COPY.actions.resendIn(cooldown.remaining)
              : AUTH_COPY.actions.resendEmail}
          </Button>
          <Button
            variant="outline"
            onPress={changeEmail}
            testID="check-inbox-change"
          >
            {AUTH_COPY.actions.changeEmail}
          </Button>
          <Link href={loginHref} asChild>
            <LinkButton role="link">{AUTH_COPY.actions.logIn}</LinkButton>
          </Link>
        </View>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={AUTH_COPY.signUp.title}
      intro={AUTH_COPY.signUp.intro}
      testID="sign-up-screen"
    >
      {banner ? <ErrorBanner view={banner} /> : null}
      <Form
        onSubmit={() => void submit()}
        accessibilityLabel={AUTH_COPY.signUp.title}
      >
        <View style={styles.form}>
          <TextField
            label={AUTH_COPY.fields.name}
            kind="name"
            value={name}
            onChangeText={setName}
            error={fieldErrors.name}
            testID="sign-up-name"
          />
          <TextField
            label={AUTH_COPY.fields.email}
            kind="email"
            value={email}
            onChangeText={setEmail}
            error={fieldErrors.email}
            testID="sign-up-email"
          />
          <PasswordField
            label={AUTH_COPY.fields.password}
            kind="new-password"
            value={password}
            onChangeText={setPassword}
            error={fieldErrors.password}
            hint={AUTH_COPY.fields.passwordRule}
            showLabel={AUTH_COPY.actions.showPassword}
            hideLabel={AUTH_COPY.actions.hidePassword}
            testID="sign-up-password"
          />
          <TextField
            label={AUTH_COPY.fields.mobile}
            kind="tel"
            value={mobile}
            onChangeText={setMobile}
            error={fieldErrors.mobile}
            hint={AUTH_COPY.fields.mobileHint}
            testID="sign-up-mobile"
          />
          {links ? <LegalLine links={links} /> : null}
          <Button
            onPress={() => void submit()}
            pending={register.pending}
            testID="sign-up-submit"
          >
            {AUTH_COPY.actions.signUp}
          </Button>
        </View>
      </Form>
      <Link href={loginHref} asChild>
        <LinkButton role="link">{AUTH_COPY.actions.logIn}</LinkButton>
      </Link>
    </AuthCard>
  );
}
